// Le récapitulatif des heures pointées CHANTIER par chantier : la répartition
// qui sert au prix de revient. Le relevé d'heures quotidien — arrivée, départ,
// pause — vit à côté, dans RelevesHeuresPanel.

import { messageErreur } from '@imp/shared/message-erreur';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { listPointagesRange, type PointageRH } from '@/modules/chantiers/api';

// 'YYYY-MM' -> bornes du mois (jours inclus).
function monthRange(ym: string): { start: string; end: string } {
  const [yStr, mStr] = ym.split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  const last = new Date(y, m, 0).getDate(); // m en base 1 -> dernier jour du mois
  return { start: `${ym}-01`, end: `${ym}-${String(last).padStart(2, '0')}` };
}

interface Recap {
  employe_id: string;
  employe_nom: string;
  normales: number;
  sup25: number;
  sup50: number;
  paniers: number;
  km: number;
  jours: number;
}

const fmt = (n: number): string => String(Math.round(n * 100) / 100);

export function PointagesChantierPanel({ mois }: { mois: string }) {
  const { start, end } = monthRange(mois);
  const q = useQuery({
    queryKey: ['pointages-range', start, end],
    queryFn: () => listPointagesRange(start, end),
  });

  const { recap, totaux } = useMemo(() => {
    const map = new Map<string, Recap>();
    for (const p of q.data ?? []) {
      const r =
        map.get(p.employe_id) ??
        ({
          employe_id: p.employe_id,
          employe_nom: p.employe_nom,
          normales: 0,
          sup25: 0,
          sup50: 0,
          paniers: 0,
          km: 0,
          jours: 0,
        } satisfies Recap);
      r.normales += p.heures_normales;
      r.sup25 += p.heures_sup_25;
      r.sup50 += p.heures_sup_50;
      r.paniers += p.paniers_repas;
      r.km += p.km;
      r.jours += 1;
      map.set(p.employe_id, r);
    }
    const list = [...map.values()].sort((a, b) => a.employe_nom.localeCompare(b.employe_nom));
    const tot = list.reduce(
      (t, r) => ({
        normales: t.normales + r.normales,
        sup25: t.sup25 + r.sup25,
        sup50: t.sup50 + r.sup50,
        paniers: t.paniers + r.paniers,
        km: t.km + r.km,
      }),
      { normales: 0, sup25: 0, sup50: 0, paniers: 0, km: 0 },
    );
    return { recap: list, totaux: tot };
  }, [q.data]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Récapitulatif par intervenant</CardTitle>
        </CardHeader>
        <CardContent>
          {q.isLoading ? (
            <p className="text-muted-foreground text-sm">Chargement…</p>
          ) : q.isError ? (
            <p className="text-destructive text-sm">{messageErreur(q.error)}</p>
          ) : recap.length === 0 ? (
            <p className="text-muted-foreground text-sm">Aucune heure saisie sur cette période.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-left text-xs uppercase">
                    <th className="py-2 pr-3">Intervenant</th>
                    <th className="py-2 pr-3 text-right">Jours</th>
                    <th className="py-2 pr-3 text-right">Normales</th>
                    <th className="py-2 pr-3 text-right">+25 %</th>
                    <th className="py-2 pr-3 text-right">+50 %</th>
                    <th className="py-2 pr-3 text-right">Total h.</th>
                    <th className="py-2 pr-3 text-right">Paniers</th>
                    <th className="py-2 text-right">Km</th>
                  </tr>
                </thead>
                <tbody>
                  {recap.map((r) => (
                    <tr key={r.employe_id} className="border-b">
                      <td className="py-2 pr-3 font-medium">{r.employe_nom}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{r.jours}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmt(r.normales)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmt(r.sup25)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{fmt(r.sup50)}</td>
                      <td className="py-2 pr-3 text-right font-semibold tabular-nums">
                        {fmt(r.normales + r.sup25 + r.sup50)}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{r.paniers}</td>
                      <td className="py-2 text-right tabular-nums">{fmt(r.km)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold">
                    <td className="py-2 pr-3">Total</td>
                    <td className="py-2 pr-3 text-right">—</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmt(totaux.normales)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmt(totaux.sup25)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmt(totaux.sup50)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {fmt(totaux.normales + totaux.sup25 + totaux.sup50)}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{totaux.paniers}</td>
                    <td className="py-2 text-right tabular-nums">{fmt(totaux.km)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {(q.data ?? []).length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Détail des pointages</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b text-left text-xs uppercase">
                  <th className="py-2 pr-3">Jour</th>
                  <th className="py-2 pr-3">Intervenant</th>
                  <th className="py-2 pr-3">Chantier</th>
                  <th className="py-2 pr-3 text-right">Normales</th>
                  <th className="py-2 pr-3 text-right">+25 %</th>
                  <th className="py-2 pr-3 text-right">+50 %</th>
                  <th className="py-2 pr-3 text-right">Paniers</th>
                  <th className="py-2 text-right">Km</th>
                </tr>
              </thead>
              <tbody>
                {(q.data ?? []).map((p: PointageRH) => (
                  <tr key={p.id} className="border-b">
                    <td className="py-1.5 pr-3 tabular-nums">
                      {new Date(p.jour).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="py-1.5 pr-3">{p.employe_nom}</td>
                    <td className="text-muted-foreground py-1.5 pr-3">
                      {p.chantier_numero} {p.chantier_libelle}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{p.heures_normales}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{p.heures_sup_25}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{p.heures_sup_50}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{p.paniers_repas}</td>
                    <td className="py-1.5 text-right tabular-nums">{p.km}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
